import { Environment } from '@environment/interfaces';
import { setEnvironmentVariables } from '@environment/utils/initialize-environment';
import { Timecard } from '../models/timecard';
import Logger from '../utils/logger';
import { getDateFromIsoFormat, getTimeFromUTCFormattedTimestamp } from '../utils/time-utils';
import { PersistenceService } from './persistence-service';

const environment: Environment = setEnvironmentVariables(process.env);

export class TimecardUploadService {

  constructor (private readonly persistenceService: PersistenceService = new PersistenceService(),
    private readonly logger = new Logger('TimecardUploadService')) {
  }

  private getFormDataForTimecard (timecard: Timecard): FormData | undefined {
    let formData
    try {
      formData = new FormData()
      formData.append('jsonMessage', JSON.stringify(timecard))
    } catch (error: any) {
      this.logger.error(`Error occurred while creating form data for timecard ${error.toString()}`)
    }
    return formData
  }

  private async deleteTimecardEvents (timecardId: number) {
    await this.persistenceService.deleteTimecardEvents(timecardId)
  }

  private async deleteTimecardIfNotCurrent (timecardId: number, currentTimecardId: number): Promise<void> {
    if (currentTimecardId === timecardId) {
      return
    }
    console.log(`Deleting timecard with id ${timecardId}`)
    await this.persistenceService.deleteTimecard(timecardId)
  }

  private validateTimecardTimestamps (timecard: Timecard): boolean {
    const timecardCreatedAt = getDateFromIsoFormat(timecard.created_at)
    const currentTime = new Date()
    if (currentTime.getTime() < timecardCreatedAt.getTime()) {
      this.logger.warn(`Timestamp tampered: Timecard created in the future. Current Time: ${currentTime.toUTCString()}, TimeCardTime: ${timecardCreatedAt.toUTCString()}`)
    }
    if (timecard.events.length > 0) {
      for (const event of timecard.events) {
        const eventCreatedAt = getDateFromIsoFormat(event.created_at)
        if (currentTime.getTime() < eventCreatedAt.getTime()) {
          this.logger.warn(`Timestamp tampered: Event created in the future. Current Time: ${currentTime.toUTCString()}, EventTime: ${eventCreatedAt.toUTCString()}`)
        }
        if (eventCreatedAt.getTime() < timecardCreatedAt.getTime()) {
          this.logger.warn(`Timestamp tampered: Event created before timecard. TimeCardTime: ${timecardCreatedAt.toUTCString()}, EventTime: ${eventCreatedAt.toUTCString()} `)
        }
      }
    }
    return true
  }

  public async uploadTimecards (currentTimecardId: number): Promise<void> {
    const timecardsToUpload = await this.persistenceService.getAllTimecards()
    for (const timecard of timecardsToUpload) {
      const timecardId = getTimeFromUTCFormattedTimestamp(timecard.created_at)
      let formData = this.getFormDataForTimecard(timecard)
      if (!formData) {
        continue
      }

      if (timecard.events.length === 0) {
        this.logger.info(`Timecard has no events left to upload ${JSON.stringify(timecard)}`)
        await this.deleteTimecardIfNotCurrent(timecardId, currentTimecardId)
        continue
      }

      if (!this.validateTimecardTimestamps(timecard)) {
        this.logger.warn(`Timecard or event timestamps are tampered, deleting timecard ${JSON.stringify(timecard)}`)
        await this.deleteTimecardIfNotCurrent(timecardId, currentTimecardId)
        continue
      }

      const postResponse = await fetch(`${environment.worksmartApiUrl}/TimecardServlet`, {
        method: 'POST',
        headers: {
          'Crossover-Account': timecard.user_id.toString()
        },
        body: formData
      })

      if (postResponse.ok) {
        await this.deleteTimecardEvents(timecardId)
        await this.deleteTimecardIfNotCurrent(timecardId, currentTimecardId)
        continue
      }

      const errorContext = {
        status: postResponse.status,
        statusText: postResponse.statusText,
        response: await postResponse.text(),
        request: formData.get('jsonMessage')
      }
      this.logger.error(`Timecard upload response is not 2xx ${errorContext}`)
    }
  }
}
